import React, { useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, Download, Share2, MapPin, Phone, Clock, 
  FileText, ArrowLeft, Home, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a01f8d12-1bde-4fd2-8a5f-4dafd263008a/artifacts/d665bj3h_Global%20Wings%20fond%20noir.png";

const POINTS_DE_VENTE = [
  {
    name: "Bureau Principal - Centre-ville",
    address: "Local 37, Immeuble CTC, Av. Wagenia",
    reference: "Réf.: Hôtel Hilton",
    phone: "+243 975 608 422"
  },
  {
    name: "Global Wings Shop - Lemba",
    address: "Av. By-Pass, Arrêt Lokole, Lemba Salongo",
    reference: "Réf.: En diagonal de l'immeuble LOKOLE et Station Salongo",
    phone: "+243 975 608 422"
  }
];

export const VisaReceiptPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const receiptRef = useRef(null);
  
  // Get data from navigation state
  const receiptData = location.state || {};
  const {
    reference = 'N/A',
    applicantName = 'N/A',
    applicantEmail = 'N/A',
    applicantPhone = 'N/A',
    visaType = 'Visa Dubaï',
    urgency = 'normal',
    travelDate = 'N/A',
    amount = 0,
    createdAt = new Date().toISOString()
  } = receiptData;

  const formattedDate = new Date(createdAt).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const handleDownload = async () => {
    if (!receiptRef.current) return;
    
    try {
      toast.loading('Génération du reçu...');
      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: '#020617',
        scale: 2
      });
      
      const link = document.createElement('a');
      link.download = `GlobalWings-Recu-${reference}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.dismiss();
      toast.success('Reçu téléchargé !');
    } catch (error) {
      toast.dismiss();
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Reçu Global Wings',
      text: `Ma demande de visa Dubaï - Réf: ${reference}\nMontant: $${amount}\nStatut: En attente de paiement`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(
          `Ma demande de visa Dubaï\nRéf: ${reference}\nMontant: $${amount}\nStatut: En attente de paiement`
        );
        toast.success('Informations copiées !');
      }
    } catch (error) {
      toast.error('Erreur lors du partage');
    }
  };

  // Redirect if no data
  if (!receiptData.reference) {
    return (
      <div className="bg-[#020617] min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <FileText className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Aucun reçu trouvé</h2>
          <p className="text-[#94A3B8] text-sm mb-6">
            Cette page est accessible après une demande de visa
          </p>
          <Link to="/visa/dubai">
            <Button className="btn-gold">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Faire une demande
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#020617] min-h-screen py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Demande Enregistrée !</h1>
          <p className="text-[#94A3B8] text-sm">
            Votre demande a été soumise. Finalisez le paiement à nos bureaux.
          </p>
        </motion.div>

        {/* Receipt Card */}
        <motion.div
          ref={receiptRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl border border-white/10 overflow-hidden mb-6"
        >
          {/* Receipt Header */}
          <div className="bg-[#D4AF37]/10 border-b border-[#D4AF37]/20 p-4">
            <div className="flex items-center justify-between">
              <img src={LOGO_URL} alt="Global Wings" className="h-20" />
              <div className="text-right">
                <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Réf.</p>
                <p className="text-lg font-mono font-bold text-[#D4AF37]">{reference}</p>
              </div>
            </div>
          </div>

          {/* Receipt Body */}
          <div className="p-4 space-y-4">
            {/* Status Badge */}
            <div className="flex items-center justify-center">
              <span className="px-4 py-1.5 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full flex items-center gap-2">
                <Clock className="w-3 h-3" />
                En attente de paiement
              </span>
            </div>

            {/* Details */}
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-[#64748B] text-xs">Client</span>
                <span className="text-white text-sm font-medium">{applicantName}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-[#64748B] text-xs">Email</span>
                <span className="text-white text-sm">{applicantEmail}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-[#64748B] text-xs">Téléphone</span>
                <span className="text-white text-sm">{applicantPhone}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-[#64748B] text-xs">Type de visa</span>
                <span className="text-white text-sm">{visaType}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-[#64748B] text-xs">Urgence</span>
                <span className={`text-sm font-medium ${urgency === 'express' ? 'text-orange-400' : 'text-green-400'}`}>
                  {urgency === 'express' ? 'Express' : 'Normal'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-[#64748B] text-xs">Date de voyage</span>
                <span className="text-white text-sm">{travelDate}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-[#64748B] text-xs">Date de demande</span>
                <span className="text-white text-sm">{formattedDate}</span>
              </div>
            </div>

            {/* Total */}
            <div className="bg-[#D4AF37]/10 rounded-xl p-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-white font-medium">Montant à payer</span>
                <span className="text-2xl font-bold text-[#D4AF37]">${amount}</span>
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-1">
                Paiement en espèces uniquement
              </p>
            </div>
          </div>

          {/* Receipt Footer */}
          <div className="bg-white/5 p-3 text-center">
            <p className="text-[10px] text-[#64748B]">
              Conservez ce reçu et présentez-le lors du paiement
            </p>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex gap-3 mb-8"
        >
          <Button 
            onClick={handleDownload}
            className="flex-1 btn-gold py-4"
            data-testid="download-receipt"
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger
          </Button>
          <Button 
            onClick={handleShare}
            className="flex-1 btn-glass py-4 text-white"
            data-testid="share-receipt"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Partager
          </Button>
        </motion.div>

        {/* Points de Vente */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="text-lg font-bold text-white">Nos Points de Vente</h3>
          </div>
          <p className="text-[#94A3B8] text-sm mb-4">
            Rendez-vous à l'un de nos bureaux pour finaliser votre paiement :
          </p>

          <div className="space-y-4">
            {POINTS_DE_VENTE.map((point, index) => (
              <div 
                key={index}
                className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-[#D4AF37]/30 transition-colors"
              >
                <h4 className="text-white font-medium mb-2">{point.name}</h4>
                <div className="space-y-1.5">
                  <p className="text-[#94A3B8] text-sm flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>{point.address}<br/><em className="text-[#64748B]">{point.reference}</em></span>
                  </p>
                  <a 
                    href={`tel:${point.phone}`}
                    className="text-[#D4AF37] text-sm flex items-center gap-2 hover:underline"
                  >
                    <Phone className="w-4 h-4" />
                    {point.phone}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* WhatsApp Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 mb-8"
        >
          <div className="flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-white font-medium text-sm mb-1">Message WhatsApp envoyé</h4>
              <p className="text-[#94A3B8] text-xs">
                Vous avez reçu un message WhatsApp avec les détails de votre demande et les adresses de nos bureaux.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="flex gap-3"
        >
          <Link to="/" className="flex-1">
            <Button className="w-full btn-glass py-3 text-white text-sm">
              <Home className="w-4 h-4 mr-2" />
              Accueil
            </Button>
          </Link>
          <Link to="/visa/dubai" className="flex-1">
            <Button className="w-full btn-glass py-3 text-white text-sm">
              <FileText className="w-4 h-4 mr-2" />
              Nouvelle demande
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default VisaReceiptPage;
